import dayjs from 'dayjs';
import { Form } from 'antd';
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import DataTableV8 from 'components/LynDataTable/DataTableV8';
import SectionCard from 'components/section/SectionCard';
import LeaseHistoryDetailModal from './LeaseHistoryDetailModal';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';

const LeaseRecordManagementV9HistoryTable = () => {
	const form = Form.useFormInstance();

	const leaseId = form.getFieldValue('leaseId');

	const [showModal, setShowModal] = useState(false);
	const [detailType, setDetailType] = useState(null);
	const [selectHistory, setSelectHistory] = useState(null);
	const [paginationModify, setPaginationModify] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [paginationEdit, setPaginationEdit] = useState({
		pageIndex: 0,
		pageSize: 10,
	});

	const [getLeaseHistoryData, { data: leaseHistoryData, isLoading: isLeaseHistoryDataLoading }] = useSendControlDataMutation();

	const COLUMNS = useMemo(
		() => [
			{
				header: 'Effective Date',
				key: 'inputChangeEffectiveDate',
				accessorFn: (row) => {
					const effectiveDate = row?.[1]?.changes?.[row?.[1]?.changes?.length - 1]?.inputChangeEffectiveDate;

					return effectiveDate ? dayjs(effectiveDate).format('YYYY-MM-DD') : 'N/A';
				},
			},
			{
				key: 'leaseTerm',
				header: 'Lease Term',
				accessorFn: (row) => row?.[1]?.leaseTerm || 'N/A',
			},
			{
				key: 'paymentModality',
				header: 'Payment Modality',
				accessorFn: (row) => {
					const value = row?.[1]?.paymentModality;

					return value === 'Start' ? 'Advance Basis' : 'Accrual Basis';
				},
			},
			{
				key: 'paymentFrequency',
				header: 'Payment Frequency',
				accessorFn: (row) => row?.[1]?.paymentFrequency || 'N/A',
			},
			{
				header: 'IBR',
				key: 'initialDiscountRate',
				accessorFn: (row) => row?.[1]?.initialDiscountRate || 'N/A',
			},
			{
				key: 'leaseStartDate',
				header: 'Lease Start Date',
				accessorFn: (row) => {
					const startDate = row?.[1]?.leaseStartDate;

					return startDate ? dayjs(startDate).format('YYYY-MM-DD') : 'N/A';
				},
			},
			{
				key: 'leaseEndDate',
				header: 'Lease End Date',
				accessorFn: (row) => {
					const endDate = row?.[1]?.leaseEndDate;

					return endDate ? dayjs(endDate).format('YYYY-MM-DD') : 'N/A';
				},
			},
		],
		[]
	);

	useEffect(() => {
		if (leaseId) {
			const payload = { leaseId };
			const convertBase64 = btoa(JSON.stringify(payload));

			getLeaseHistoryData({
				payload,
				data: convertBase64,
				processCode: '10147',
				scriptName: 'lease-edit-modify-history',
			});
		}
	}, [leaseId]);

	const modifiedData = leaseHistoryData?.responseData?.modifiedData || [];
	const editData = leaseHistoryData?.responseData?.editedData || [];

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
			<SectionCard
				sx={{
					padding: '16px',
					borderRadius: '10px',
					border: '1px solid #e6ebf2',
				}}
			>
				<Typography
					variant="h4"
					sx={{
						color: '#1a3b5d',
						padding: '0 0 12px 0',
						fontWeight: 600,
						fontSize: '18px',
						borderBottom: '1px solid #e0e6ed',
						marginBottom: '16px',
					}}
				>
					Modify History
				</Typography>

				<DataTableV8
					manualPagination
					COLUMNS={COLUMNS}
					DATA={modifiedData}
					firstColStrick={false}
					pagination={paginationModify}
					totalCount={modifiedData.length}
					isLoading={isLeaseHistoryDataLoading}
					xlsxFileName={`${leaseId}_modifiedData`}
					onPaginationChange={setPaginationModify}
					onClickHandler={(data) => {
						setShowModal(true);
						setSelectHistory(data);
						setDetailType('modify');
					}}
				/>
			</SectionCard>

			<SectionCard
				sx={{
					padding: '16px',
					borderRadius: '10px',
					border: '1px solid #e6ebf2',
				}}
			>
				<Typography
					variant="h4"
					sx={{
						color: '#1a3b5d',
						padding: '0 0 12px 0',
						fontWeight: 600,
						fontSize: '18px',
						borderBottom: '1px solid #e0e6ed',
						marginBottom: '16px',
					}}
				>
					Edit History
				</Typography>

				<DataTableV8
					DATA={editData}
					manualPagination
					COLUMNS={COLUMNS}
					firstColStrick={false}
					pagination={paginationEdit}
					totalCount={editData.length}
					xlsxFileName={`${leaseId}_editData`}
					isLoading={isLeaseHistoryDataLoading}
					onPaginationChange={setPaginationEdit}
					onClickHandler={(data) => {
						setShowModal(true);
						setDetailType('edit');
						setSelectHistory(data);
					}}
				/>
			</SectionCard>

			{showModal && (
				<LeaseHistoryDetailModal
					showModal={showModal}
					detailType={detailType}
					setShowModal={setShowModal}
					setDetailType={setDetailType}
					selectHistory={selectHistory}
					setSelectHistory={setSelectHistory}
				/>
			)}
		</Box>
	);
};

export default LeaseRecordManagementV9HistoryTable;
